import { useContext, useEffect, useState } from 'react'

import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
} from 'chart.js'
import { Pie } from 'react-chartjs-2'
import { Bar } from 'react-chartjs-2'

import {
  Card,
  Select,
  SelectAction,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectValue,
} from 'keep-react'
import { DateDropDownItem, getDateRanges } from './datesDropdown'
import { AuthContext } from 'contexts/AuthContext'
import { BarChartData, PieChartData } from 'api/types/graph'
import { graphService } from 'api/services/graph'
import { RocketPrime } from 'components/Icons/rocketPrime'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ArcElement,
  Tooltip,
  Legend,
)

interface FormatedChartData {
  type: 'PIE' | 'BAR'
  data: {
    labels: string[]
    datasets: {
      label: string
      data: number[]
      backgroundColor: string[] | string
    }[]
  }
}

export const ChartsArea = () => {
  const [charts, setCharts] = useState<Array<
    PieChartData[] | BarChartData[] | []
  > | null>(null)
  const [chartData, setChartData] = useState<FormatedChartData[] | null>(null)

  const { currentBroker, currentUser } = useContext(AuthContext)
  const dropDownDates: DateDropDownItem[] = Object.values(getDateRanges())

  const getChartsData = async (startDate: string, endDate: string) => {
    if (!currentBroker) return
    try {
      const response = (await graphService.getCharts({
        startDate,
        endDate,
        brokerNo: currentBroker?.BrokerNo as number,
      })) as unknown as Array<PieChartData[] | BarChartData[]>
      setCharts(response)
    } catch (error) {
      console.error('Error fetching charts data:', error)
    }
  }

  const pieChartColors = [
    '#B0191A',
    '#E11F26',
    '#EC6152',
    '#F29682',
    '#DADADA',
    '#454444',
  ]

  const pieChartColorsPrime = [
    '#E7D7B4',
    '#DDC79B',
    '#D2B683',
    '#AD8B50',
    '#C8A66A',
    '#927035',
  ]

  const barChartColors = ['#B0191A', '#F29682']

  const barChartColorsPrime = ['#E7D7B4', '#AD8B50']

  const handleDateSelection = (value: string) => {
    const selectedRange = dropDownDates.find(item => item.value === value)
    if (!selectedRange) return
    const { start, end } = selectedRange
    getChartsData(start, end)
  }

  useEffect(() => {
    const startDate = dropDownDates[3].start
    const endDate = dropDownDates[3].end
    if (!currentBroker) return

    getChartsData(startDate, endDate)
  }, [])

  useEffect(() => {
    const startDate = dropDownDates[3].start
    const endDate = dropDownDates[3].end
    if (!currentBroker || !currentUser) return

    getChartsData(startDate, endDate)
  }, [currentBroker, currentUser])

  useEffect(() => {
    if (!charts || charts.length === 0) return

    const data: Array<FormatedChartData | null> = charts
      .filter(chart => chart.length > 0)
      .map((chart: PieChartData[] | BarChartData[] | []) => {
        let label = ''
        let labels = [] as string[]
        let values = [] as number[]
        let values1 = [] as number[]
        let values2 = [] as number[]
        let backgroundColor = [] as string[]

        if (chart && chart[0].TYPE === 'PIE') {
          label = chart[0].TITLE

          const c = chart as PieChartData[]

          c.forEach((item: PieChartData, index: number) => {
            labels.push(item.Produto)
            if (typeof item['Valor Faturado'] === 'number') {
              values.push(item['Valor Faturado'])
            }
            backgroundColor.push(
              currentBroker?.IsPrimePartner
                ? pieChartColorsPrime[index % pieChartColorsPrime.length]
                : pieChartColors[index % pieChartColors.length],
            )
          })

          const chartData: FormatedChartData = {
            type: 'PIE',
            data: {
              labels: labels,
              datasets: [
                {
                  label: label,
                  data: values,
                  backgroundColor: backgroundColor,
                },
              ],
            },
          }

          return chartData
        } else if (chart && 'TITLE' in chart[0]) {
          label = chart[0].TITLE

          const c = chart as BarChartData[]

          c.forEach((item: BarChartData, index: number) => {
            labels.push(item.Categoria)
            values1.push(item['Apólices submetidas'])
            values2.push(item['Apólices concretizadas'])
            backgroundColor.push(
              currentBroker?.IsPrimePartner
                ? barChartColorsPrime[index % barChartColorsPrime.length]
                : barChartColors[index % barChartColors.length],
            )
          })

          const chartData: FormatedChartData = {
            type: 'BAR',
            data: {
              labels: labels as string[],
              datasets: [
                {
                  label: 'Apólices submetidas',
                  data: values1,
                  backgroundColor: currentBroker?.IsPrimePartner
                    ? barChartColorsPrime[0]
                    : barChartColors[0],
                },
                {
                  label: 'Apólices concretizadas',
                  data: values2,
                  backgroundColor: currentBroker?.IsPrimePartner
                    ? barChartColorsPrime[1]
                    : barChartColors[1],
                },
              ],
            },
          }

          return chartData
        }

        return null
      })

    setChartData(data.filter((d): d is FormatedChartData => d !== null))
  }, [charts])

  if (!chartData) return <></>

  return (
    <div className="pt-6 mb-12">
      <Card className="w-full max-w-none p-4">
        <header className="mb-12 flex items-center justify-between">
          <h2 className="text-lg uppercase pb-3 leading-none text-black flex items-center gap-1">
            {currentBroker?.IsPrimePartner ? <RocketPrime /> : '<Rocket />'}

            <span>Segurómetro</span>
          </h2>
          <Select
            defaultValue="currentYear"
            onValueChange={handleDateSelection}
          >
            <SelectAction className="w-[20rem]">
              <SelectValue placeholder="Seleccione um período" />
            </SelectAction>
            <SelectContent>
              <SelectGroup>
                {dropDownDates.map((item, index) => (
                  <SelectItem key={index} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </header>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {chartData
            ?.filter(chart => chart.type === 'PIE')
            .map((chart, index) => {
              const options = {
                responsive: true,
                plugins: {
                  legend: {
                    position: 'right' as const,
                  },
                  title: {
                    display: true,
                    text: chart.data.datasets[0].label,
                  },
                },
              }
              return (
                <div
                  key={index}
                  className="shadow-md bg-white rounded-xl p-4 w-full h-[600px]"
                >
                  {chart.type === 'PIE' && (
                    <Pie data={chart.data} options={options} />
                  )}
                </div>
              )
            })}
          {chartData
            ?.filter(chart => chart.type === 'BAR')
            .map((chart, index) => {
              const options = {
                responsive: true,
                plugins: {
                  legend: {
                    position: 'top' as const,
                  },
                  title: {
                    display: true,
                    text: chart.data.datasets[0].label,
                  },
                },
              }
              return (
                <div
                  key={index}
                  className="shadow-md bg-white rounded-xl p-4 w-full col-span-2"
                >
                  {chart.type === 'BAR' && (
                    <Bar options={options} data={chart.data} />
                  )}
                </div>
              )
            })}
        </div>
      </Card>
    </div>
  )
}
